import React from "react";
import Dropzone, { type Accept } from "react-dropzone";
import * as Sentry from "@sentry/react";
import classnames from "classnames";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import type { ApolloClient } from "@apollo/client";
import { each, find, isEmpty, isNil, map, omit, slice, some } from "lodash";
import { EMPLOYEES_UPLOAD_DOCUMENT } from "statics/docTypes";
import { FiFolderPlus } from "react-icons/fi";

import { fileService } from "services/uploaderService";
import { persistDocument } from "actions/userActions";

import Button from "components/Button";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";

import "./FileUploader.scss";

/** A file that has been accepted and is being / has been uploaded. */
interface UploadedFile {
  fileName: string;
  fileUrl: string;
  file: File;
  signedRequest: string;
}

/** Per-file upload progress entry keyed by file name. */
interface UploadProgress {
  status: string;
  percent: number;
}

interface FileUploaderOwnProps {
  metadata?: object;
  onSuccess?: () => void;
  fileLimit?: number;
  docType?: string;
  subDocType?: string;
  persist?: boolean;
  requireSubDocType?: boolean;
  hideUploadedFiles?: boolean;
  fileUploadedMsg?: string;
  documentFace?: string;
}

const mapStateToProps = () => ({});

const mapDispatchToProps = {
  persistDocument,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type FileUploaderProps = PropsFromRedux & FileUploaderOwnProps;

interface FileUploaderState {
  uploadedFiles: UploadedFile[];
  uploadProgress: Record<string, UploadProgress>;
  complete: boolean;
  errors: Record<string, string | null>;
}

class FileUploader extends React.PureComponent<
  WithApolloClient<FileUploaderProps>,
  FileUploaderState
> {
  static defaultProps = {
    fileLimit: 1,
    docType: "DOCUMENT",
    persist: false,
    requireSubDocType: false,
  };

  constructor(props: WithApolloClient<FileUploaderProps>) {
    super(props);

    this.state = {
      uploadedFiles: [],
      uploadProgress: {},
      complete: false,
      errors: {},
    };
  }

  // eslint-disable-next-line @typescript-eslint/require-await -- preserved as async; body is callback/then-driven with no await
  async getSignedUrl(files: File[]) {
    const filesToUpload = slice(files, 0, this.props.fileLimit);
    // get max number of files
    // upload files
    //
    each(filesToUpload, (file) => {
      if (this.validateSize(file)) {
        if (!this.duplicateFile(file)) {
          fileService
            .getUploadUrl(
              this.props.client as ApolloClient<object>,
              file.name,
              file.type
            )
            .then(
              (data) => {
                const newFile = {
                  fileName: file.name,
                  fileUrl: data.url,
                  file,
                  signedRequest: data.signedRequest,
                };

                const newProgress = { ...this.state.uploadProgress };
                newProgress[file.name] = {
                  status: "Pending",
                  percent: 1,
                };

                this.setState({
                  uploadedFiles: this.state.uploadedFiles.concat(newFile),
                  uploadProgress: newProgress,
                  errors: {
                    ...this.state.errors,
                    [file.name]: null,
                  },
                });

                // @ts-expect-error -- uploadFile declares `this: { httprequest? }`;
                // calling it as fileService.uploadFile binds method-this to the
                // service object, which the typed signature rejects. Runtime is
                // unaffected (the body only assigns this.httprequest).
                void fileService.uploadFile(newFile, this);
              },
              () => {
                // Cleanup here isn't needed, file and objects were never created
                this.setState({
                  errors: {
                    ...this.state.errors,
                    [file.name]: "Upload failed.",
                  },
                });
              }
            );
        } else {
          this.setState({
            errors: {
              ...this.state.errors,
              [file.name]: "Duplicate file.",
            },
          });
        }
      } else {
        this.setState({
          errors: {
            ...this.state.errors,
            [file.name]: "File size cannot exceed 12mb.",
          },
        });
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/require-await -- preserved as async; body has no await
  async storeUploadedDocumentData(file: File) {
    const theFile = this.getFileFromState(file);

    if (this.props.persist) {
      const docResponse = fileService.uploadDocument(
        this.props.client as ApolloClient<object>,
        theFile!.fileUrl,
        theFile!.fileName,
        this.props.docType!,
        theFile!.file.type
      );
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- PRESERVED BUG: uploadDocument returns a Promise, so `!docResponse` is always false and this branch is dead code. Faithful to original; do not "fix" without product sign-off.
      if (!docResponse) {
        this.errorCleanup("Upload submission failed", file);
      }
    }
    if (this.props.fileLimit! <= this.state.uploadedFiles.length) {
      // We're done, set state and update forms
      this.setState({ complete: true });
    }

    // Call Action/Reducer to add this to global state
    this.props.persistDocument({
      file: theFile!,
      docType: this.props.docType,
      subDocType: this.props.subDocType,
      metadata: this.props.metadata,
      documentFace: this.props.documentFace,
    });
    if (this.props.onSuccess) {
      this.props.onSuccess();
    }
  }

  onFinish = function (this: FileUploader, file: File) {
    void this.storeUploadedDocumentData(file);
  };

  onProgress = function (
    this: FileUploader,
    percent: number,
    status: string,
    file: File
  ) {
    const copy = { ...this.state.uploadProgress };
    copy[file.name] = {
      status,
      percent,
    };
    this.setState({ uploadProgress: copy });
  };

  onError = function (this: FileUploader, status: string, file: File) {
    this.errorCleanup(status, file);
  };

  errorCleanup = function (this: FileUploader, message: string, file: File) {
    // Cleanup here involves removing the uploadedFiles object, and uploadProgress object
    const copy = { ...this.state.uploadProgress };
    const newCopy = omit(copy, file.name);
    Sentry.captureMessage(
      `Document upload failed: ${message}, for file ${file.name}`,
      { level: "warning" }
    );
    this.setState({
      uploadedFiles: this.state.uploadedFiles.filter(
        (storedFile) => storedFile.file !== file
      ),
      uploadProgress: newCopy,
      errors: {
        ...this.state.errors,
        [file.name]: message,
      },
    });
  };

  validateSize = function (file: File) {
    //12 000 000 == 12MB
    if (file.size <= 12000000) {
      return true;
    }
    return false;
  };

  duplicateFile = function (this: FileUploader, file: File) {
    if (some(this.state.uploadedFiles, { fileName: file.name })) {
      return true;
    }
    return false;
  };

  getFileFromState(file: File) {
    return find(this.state.uploadedFiles, { file });
  }

  verificationCheckMark = () => {
    return (
      <svg
        className="checkmark"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 52 52"
      >
        <circle
          className="checkmark__circle"
          cx="26"
          cy="26"
          r="25"
          fill="none"
        />
        <path
          className="checkmark__check"
          fill="none"
          d="M14.1 27.2l7.1 7.2 16.7-16.8"
        />
      </svg>
    );
  };

  getProcessingFile = () => {
    const uploadedFile = this.state.uploadedFiles.length > 0;

    if (!uploadedFile) {
      return (
        <div className="noUploads">
          <div className="fileupload-wrapper">
            <div className="upload-circle">
              <FiFolderPlus
                // preserved as-is: original had a stray space inside the hex string
                fill={"# f2f4f8"}
                color={"#f2f4f8"}
                stroke={"#7F97B7"}
                fontSize={18}
                strokeWidth={"1.5"}
              />
            </div>
          </div>
          <span className="files">
            <p className="drag-drop">
              Drag & Drop {this.props.fileLimit === 1 ? "File" : "Files"},
            </p>
            <p className="or">or</p>
          </span>
        </div>
      );
    } else if (uploadedFile) {
      return (
        <div className="complete">
          <span className="check">
            <span>{this.verificationCheckMark()}</span>
          </span>
          <span>
            <p className="file-header">Your file was uploaded! </p>
            <p className="subtext">{this.props.fileUploadedMsg}</p>
          </span>
        </div>
      );
    }
    return <div></div>;
  };

  renderFile(fileName: string) {
    return (
      <div className="file-type">
        <img className="png" src="/assets/file.png"></img>
        <div className="file-name">{fileName}</div>
      </div>
    );
  }

  renderProgress(progress: UploadProgress) {
    const displayStatus = progress.status != "Complete";

    return progress ? (
      <div className="file-progress">
        <span className="status">
          <div className="status-box">
            {progress.percent}% - {progress.status}
          </div>
          {displayStatus && <IconSpinner size="sm" />}
        </span>
      </div>
    ) : null;
  }

  render() {
    const uploadedFile = this.state.uploadedFiles.length > 0;
    const hasNotUploadedAllFiles =
      this.props.fileLimit! > this.state.uploadedFiles.length;
    const hasNoSubType =
      this.props.requireSubDocType &&
      (isNil(this.props.subDocType) || isEmpty(this.props.subDocType));
    const csvContentType = "text/csv";
    const excelContentTypes =
      "application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const acceptedFileTypes: Accept =
      this.props.docType === EMPLOYEES_UPLOAD_DOCUMENT
        ? { [csvContentType]: [], [excelContentTypes]: [] }
        : { "image/jpeg": [], "image/png": [], "application/pdf": [] };

    const filesUploaded = this.state.uploadedFiles.length;

    const showFileUploadedText = filesUploaded !== 0;

    let btnLabel: string | undefined;
    if (hasNotUploadedAllFiles && filesUploaded !== 0) {
      btnLabel = "Add another file";
    } else if (hasNotUploadedAllFiles) {
      btnLabel = "Browse Files";
    }

    return (
      <div className="file-drop-zone">
        <Dropzone
          noKeyboard={true}
          accept={acceptedFileTypes}
          onDrop={(acceptedFiles) => void this.getSignedUrl(acceptedFiles)}
          noClick={!hasNotUploadedAllFiles}
        >
          {({ getRootProps, getInputProps }) => (
            <div className="container">
              <div
                {...getRootProps()}
                className={classnames("file-uploader", {
                  ["complete-file-upload"]: uploadedFile,
                })}
              >
                <div> {this.getProcessingFile()}</div>
                <input data-testid="file-input" {...getInputProps()} />

                {hasNotUploadedAllFiles &&
                  this.state.uploadedFiles.length > 0 && <br />}
                {hasNotUploadedAllFiles && (
                  <Button
                    type="button"
                    name="submit"
                    size="sm"
                    withArrow={false}
                    disabled={hasNoSubType}
                  >
                    {btnLabel}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Dropzone>
        <div>
          {!this.props.hideUploadedFiles && showFileUploadedText && (
            <p className="uploaded">Uploaded Files</p>
          )}
          <span>
            {!this.props.hideUploadedFiles &&
            this.state.uploadedFiles.length > 0 ? (
              <div className="uploaded-files">
                <div className="file-box">
                  {this.state.uploadedFiles.map((uploadedFile, index) => {
                    return (
                      <span className="file" key={index}>
                        <span>{this.renderFile(uploadedFile.fileName)}</span>
                        <span>
                          {this.renderProgress(
                            this.state.uploadProgress[uploadedFile.fileName]
                          )}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </span>
          <span>
            {map(this.state.errors, (err, fileName) => {
              if (!err) return null;
              const msg = `Error uploading ${fileName} - ${err}`;
              return (
                <div>
                  <p className="uploaded">Uploaded Files</p>
                  <Alert type="error" msg={msg} />
                </div>
              );
            })}
          </span>
        </div>
      </div>
    );
  }
}

export default connector(withApollo<FileUploaderProps>(FileUploader));
